import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { User } from '../types';
import { authService } from '../services/api';
import { BASE_URL } from '../apiConfig';
import { getTenantHeaders } from '../config/tenant';
import { getDeviceId } from '../utils/deviceId';
import { clearAllCaches } from '../hooks/useCourseApi';
import { clearContactCache } from '../hooks/useContact';
import { STORAGE_KEYS } from '../constants/storage';
import { isTokenExpired } from '../utils/token';
import axios from 'axios';
import { setRefreshTokenCallback, clearRefreshTokenCallback } from '../utils/authRefreshCallback';
import { AuthErrorType, createAuthError, createNetworkError, createTokenExpiredError, createInvalidCredentialsError, createMaxDevicesError } from '../types/auth';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
    isUnconfirmedEmail?: boolean;
    isMaxDevicesError?: boolean;
    userId?: string;
  }>;
  register: (formData: FormData) => Promise<{success: boolean, error?: string}>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  resetPasswordWithOtp: (email: string, otp: string, newPassword: string, confirmPassword: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  updateProfile: (user: Partial<User>) => void;
  refreshToken: () => Promise<string | null>;
  checkEnrolledCourses: (options?: { forceRefresh?: boolean; userId?: string; token?: string }) => Promise<boolean>;
  hasEnrolledCourses: boolean | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  validateAuthState: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Skip Auth/validate fetch once we know it 404s (endpoint missing) to avoid redundant round-trips. */
let validateEndpointKnown404 = false;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

interface LoginResponse {
  user: {
    id: string;
    name: string;
    email: string;
    phoneNumber: string;
    academicYear: string;
    registrationDate: string;
  };
  token: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasEnrolledCourses, setHasEnrolledCourses] = useState<boolean | null>(null);
  
  // Map to track pending validation requests and prevent duplicates
  const pendingValidations = new Map<string, Promise<boolean>>();
  
  // Validation cache with TTL (5 minutes)
  const validationCache = {
    result: false,
    timestamp: 0,
    TTL: 5 * 60 * 1000 // 5 minutes in milliseconds
  };
  
  // Add function to update authentication state
  const updateAuthState = (authenticated: boolean, user: User | null = null, token: string | null = null) => {
    // Validate token before storing if provided
    if (authenticated && token && isTokenExpired(token)) {
      console.warn('Token is expired, clearing auth state');
      authenticated = false;
      user = null;
      token = null;
    }
    
    setIsAuthenticated(authenticated);
    setCurrentUser(user);
    
    if (authenticated && user && token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.IS_AUTHENTICATED, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
      localStorage.removeItem(STORAGE_KEYS.IS_AUTHENTICATED);
    }
  };

  // Function to validate current auth state
  const validateAuthState = async (): Promise<boolean> => {
    console.log('Validating auth state...');
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    const savedIsAuthenticated = localStorage.getItem(STORAGE_KEYS.IS_AUTHENTICATED);
    
    console.log('Current auth state:', {
      hasToken: !!token,
      hasUser: !!savedUser,
      isAuthenticatedInStorage: savedIsAuthenticated === 'true',
      isAuthenticatedInState: isAuthenticated,
      hasCurrentUser: !!currentUser
    });

    if (!token || !savedUser || savedIsAuthenticated !== 'true') {
      console.log('Missing auth data, clearing auth state');
      updateAuthState(false);
      validationCache.result = false;
      validationCache.timestamp = Date.now();
      return false;
    }

    // Check validation cache first
    const now = Date.now();
    if (validationCache.result && (now - validationCache.timestamp) < validationCache.TTL) {
      console.log('Using cached validation result');
      return validationCache.result;
    }

    try {
      // Parse saved user to get ID
      const user = JSON.parse(savedUser);
      
      // If user state is already properly set and localStorage says authenticated,
      // trust it and skip API validation (performance optimization)
      if (currentUser && isAuthenticated && currentUser.id === user.id) {
        console.log('User already authenticated and state is correct, skipping validation');
        return true;
      }
      
      // Create a unique key for this validation request to prevent duplicates
      const validationKey = `validation_${user.id}`;
      
      // Check if there's already a pending validation for this user
      if (pendingValidations.has(validationKey)) {
        console.log('Using existing validation request');
        return pendingValidations.get(validationKey)!;
      }

      console.log('Making validation request...', {
        userId: user.id,
        hasToken: !!token,
        reason: 'State not properly set or user mismatch'
      });

      // Try dedicated auth validation endpoint first, fallback to enrolled courses
      const validationPromise = (async () => {
        if (!validateEndpointKnown404) {
          try {
            const validateUrl = `${BASE_URL}api/Auth/validate`;
            const dedicatedResponse = await fetch(validateUrl, {
              method: 'GET',
              headers: {
                'accept': '*/*',
                'Authorization': `Bearer ${token}`,
                ...getTenantHeaders()
              }
            });
            if (dedicatedResponse.status === 404) {
              validateEndpointKnown404 = true;
            }
            if (dedicatedResponse.status !== 404) {
              return dedicatedResponse;
            }
          } catch (error) {
            // continue to fallback
          }
        }
        
        // Fallback to enrolled courses endpoint
        const url = new URL(`${BASE_URL}api/Student/Student-Enrolled-Courses`);
        url.searchParams.append('studentId', user.id);
        url.searchParams.append('pagenumber', '1');
        url.searchParams.append('pagesize', '1'); // Minimal data for validation

        return await fetch(url, {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`,
            ...getTenantHeaders()
          }
        });
      })().then(response => {
        console.log('Validation response:', {
          status: response.status,
          ok: response.ok
        });

        if (!response.ok) {
          if (response.status === 404) {
            // For 404, keep the user logged in but log the error
            console.log('No enrolled courses found, but keeping user logged in');
            // Ensure user state is set
            if (!currentUser || !isAuthenticated) {
              setCurrentUser(user);
              setIsAuthenticated(true);
              localStorage.setItem(STORAGE_KEYS.IS_AUTHENTICATED, 'true');
            }
            return true;
          }
          
          // For other error statuses (like 401, 403, 500, etc.)
          console.log('Token validation failed, clearing auth state');
          updateAuthState(false);
          validationCache.result = false;
          validationCache.timestamp = Date.now();
          return false;
        }

        // Token is valid, ensure user state is set
        if (!currentUser || !isAuthenticated) {
          console.log('Restoring user state after successful validation');
          setCurrentUser(user);
          setIsAuthenticated(true);
          localStorage.setItem(STORAGE_KEYS.IS_AUTHENTICATED, 'true');
        }

        // Update cache
        validationCache.result = true;
        validationCache.timestamp = Date.now();
        return true;
      }).catch(error => {
        console.error('Token validation error:', error);
        updateAuthState(false);
        validationCache.result = false;
        validationCache.timestamp = Date.now();
        return false;
      }).finally(() => {
        pendingValidations.delete(validationKey);
      });

      pendingValidations.set(validationKey, validationPromise);
      return validationPromise;
    } catch (error) {
      console.error('Token validation error:', error);
      updateAuthState(false);
      validationCache.result = false;
      validationCache.timestamp = Date.now();
      return false;
    }
  };
  
  // Check for saved user and token on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('Starting auth initialization...');
      setIsLoading(true);
      try {
        const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        const savedIsAuthenticated = localStorage.getItem(STORAGE_KEYS.IS_AUTHENTICATED);
        
        console.log('Saved auth data:', { 
          hasUser: !!savedUser, 
          hasToken: !!token,
          isAuthenticated: !!savedIsAuthenticated
        });
        
        if (savedUser && token && savedIsAuthenticated === 'true') {
          const user = JSON.parse(savedUser);
          
          // Set initial state based on saved data
          setCurrentUser(user);
          setIsAuthenticated(true);
          
          // Validate the token with the backend
          const isValid = await validateAuthState();
          if (!isValid) {
            console.log('Initial token validation failed');
            updateAuthState(false);
          } else {
            console.log('Initial token validation successful');
          }
        } else {
          console.log('No saved auth data found');
          updateAuthState(false);
        }
      } catch (error) {
        console.error('Error during auth initialization:', error);
        updateAuthState(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []); // Only run once on mount

  // Update refresh token callback when refreshToken function changes
  useEffect(() => {
    setRefreshTokenCallback(refreshToken);
    return () => {
      clearRefreshTokenCallback();
    };
  }, [currentUser]); // Re-register when currentUser changes

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await authService.login(email, password);
      
      if (result.success && result.data) {
        const { user, token } = result.data as LoginResponse;

        const userData: User = {
          id: user.id,
          name: user.name,
          email: user.email,
          password: '',
          grade: user.academicYear,
          enrolledCourses: [],
          phoneNumber: user.phoneNumber
        };
        
        updateAuthState(true, userData, token);
        localStorage.setItem(STORAGE_KEYS.USER_ID, user.id);
        // Clear enrollment cache so post-login checkEnrolledCourses fetches fresh data for this user
        setHasEnrolledCourses(null);
        return { success: true, userId: user.id };
      }
      
      // Pass through any special flags like isMaxDevicesError
      const errorMessage = result.error || 'Login failed';
      const authError = result.isMaxDevicesError 
        ? createMaxDevicesError()
        : errorMessage.includes('Invalid credentials') || errorMessage.includes('البريد الإلكتروني أو الباسورد')
        ? createInvalidCredentialsError()
        : createAuthError(AuthErrorType.UNKNOWN, errorMessage);
      
      return {
        success: false,
        error: authError.message,
        isMaxDevicesError: result.isMaxDevicesError
      };
    } catch (error) {
      console.error('Login error:', error);
      const authError = createNetworkError(error);
      return { success: false, error: authError.message };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (formData: FormData): Promise<{success: boolean, error?: string}> => {
    setIsLoading(true);
    
    try {
      const result = await authService.register(formData);
      
      if (result.success && result.data) {
        // Don't set user or authentication state after registration
        // User needs to verify email and then log in
        return { success: true };
      }
      
      return { 
        success: false, 
        error: result.error || 'Registration failed'
      };
    } catch (error: any) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.message || 'An unexpected error occurred'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Clear auth state immediately before API call to prevent race conditions
      updateAuthState(false);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      validateEndpointKnown404 = false;
      // Invalidate validation cache on logout
      validationCache.result = false;
      validationCache.timestamp = 0;
      // Clear enrolled courses cache
      setHasEnrolledCourses(null);
      
      // Clear all caches to prevent showing previous user's data
      clearAllCaches();
      clearContactCache();
      // Clear sessionStorage as well
      sessionStorage.clear();
      
      // Then call API logout (fire and forget - don't wait for it)
      authService.logout().catch(error => {
        console.error('Logout API call failed:', error);
        // Already cleared local state, so just log the error
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Ensure everything is cleared even if there's an error
      updateAuthState(false);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      clearAllCaches();
      clearContactCache();
      sessionStorage.clear();
    }
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      // Use authService which is already configured with axios
      const response = await authService.resetPassword(email);
      
      if (response.success) {
        return { 
          success: true, 
          message: response.message || 'OTP has been sent to your email.' 
        };
      } else {
        return { 
          success: false, 
          error: response.error || 'Password reset failed.' 
        };
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      return { 
        success: false, 
        error: 'An unexpected error occurred. Please try again later.' 
      };
    }
  };

  const resetPasswordWithOtp = async (
    email: string, 
    otp: string, 
    newPassword: string, 
    confirmPassword: string
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await authService.resetPasswordWithOtp(
        email,
        otp,
        newPassword,
        confirmPassword
      );
      
      return response;
    } catch (error: any) {
      console.error('Reset password with OTP error:', error);
      return { 
        success: false, 
        error: 'An unexpected error occurred. Please try again later.' 
      };
    }
  };

  const updateProfile = (userUpdates: Partial<User>) => {
    if (!currentUser) return;

    const updatedUser = { ...currentUser, ...userUpdates };
    setCurrentUser(updatedUser as User);
    localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(updatedUser));
  };

  // Centralized enrolled courses check with caching
  const checkEnrolledCourses = async (options?: { forceRefresh?: boolean; userId?: string; token?: string }): Promise<boolean> => {
    const { forceRefresh = false, userId: optionsUserId, token: optionsToken } = options ?? {};
    // Return cached value if available (unless forceRefresh, e.g. right after login)
    if (!forceRefresh && hasEnrolledCourses !== null) {
      return hasEnrolledCourses;
    }

    try {
      const token = optionsToken ?? localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const userId = optionsUserId ?? localStorage.getItem(STORAGE_KEYS.USER_ID);

      if (!token || !userId) {
        setHasEnrolledCourses(false);
        return false;
      }

      interface EnrolledCoursesResponse {
        success: boolean;
        message?: string;
        data?: {
          totalCount: number;
          courses: any[];
        };
      }

      const response = await axios.get<EnrolledCoursesResponse>(
        `${BASE_URL}api/Student/Student-Enrolled-Courses?studentId=${userId}&pagenumber=1&pagesize=1`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...getTenantHeaders()
          }
        }
      );

      const data = response.data?.data;
      const hasCourses = !!(response.data?.success && data && (
        (typeof data.totalCount === 'number' && data.totalCount > 0) ||
        (Array.isArray(data.courses) && data.courses.length > 0)
      ));

      setHasEnrolledCourses(hasCourses);
      return hasCourses;
    } catch (error) {
      console.error('Error checking enrolled courses:', error);
      setHasEnrolledCourses(false);
      return false;
    }
  };

  // Centralized token refresh that updates both state and storage
  const refreshToken = async (): Promise<string | null> => {
    try {
      const currentToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (!currentToken) {
        console.warn('No token available for refresh');
        updateAuthState(false);
        return null;
      }

      const API_URL = 'https://api.mromarelkholy.com';

      const response = await axios.post(`${API_URL}/api/Auth/refresh-token`, {}, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'DeviceId': getDeviceId(),
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...getTenantHeaders()
        }
      });

      if ((response.data as any).success) {
        const { token } = (response.data as any).data;
        console.log('Token refreshed successfully');
        
        // Update both localStorage AND AuthContext state
        if (currentUser) {
          updateAuthState(true, currentUser, token);
        } else {
          // If no current user, just update the token
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        }
        
        return token;
      } else {
        console.error('Refresh token response was not successful');
        updateAuthState(false);
        return null;
      }
    } catch (error: any) {
      console.error('Token refresh error:', error);
      // If refresh fails with 401/403, clear auth state
      if (error.response?.status === 401 || error.response?.status === 403) {
        updateAuthState(false);
      }
      return null;
    }
  };

  const value = {
    currentUser,
    login,
    register,
    logout,
    resetPassword,
    resetPasswordWithOtp,
    updateProfile,
    refreshToken,
    checkEnrolledCourses,
    hasEnrolledCourses,
    isAuthenticated,
    isLoading,
    validateAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 